import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/** Height of the sticky public header (h-16) plus a little breathing room. */
const HEADER_OFFSET = 80;

/**
 * React Router's <Link>/<NavLink> change the URL but never scroll to a `#hash`
 * target. Mount this inside a routed shell so anchor links like `/#features`
 * actually jump to their section — whether navigating from another page or
 * clicking the link while already on the page.
 */
export function ScrollToHash() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      // Plain navigation (no hash) → return to the top of the new page.
      window.scrollTo({ top: 0 });
      return;
    }

    const id = decodeURIComponent(hash.slice(1));

    // The target may not be painted yet (lazy page chunk still resolving), so
    // retry across a few frames before giving up.
    let frames = 0;
    let raf = 0;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
        window.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' });
        return;
      }
      if (frames++ < 10) raf = requestAnimationFrame(tryScroll);
    };
    raf = requestAnimationFrame(tryScroll);

    return () => cancelAnimationFrame(raf);
  }, [pathname, hash]);

  return null;
}
